/**
 * The settings the backoffice writes for one screen. A screen nobody has configured
 * still has to work, so every field falls back to the same default the backoffice
 * offers — the record's absence is not an error.
 */
import { type ScreenSettings, screenSchema } from './configuration'
import type { DisplayMode } from './slips'
export type KdsScreenSettings = ScreenSettings
export const DEFAULT_SETTINGS: KdsScreenSettings = screenSchema.parse({})

export type TillColor = { tillId: string; color: string; label: string | null }

type Row = { [key: string]: unknown }

/**
 * A record written by an older backoffice is missing whatever was added since, and a
 * record written by a newer one may carry a value this build has never heard of. Both
 * fall back rather than throw: a kitchen screen that refuses to draw is worse than one
 * drawing a setting at its default.
 */
export function readScreenSettings(row: Row | null | undefined): KdsScreenSettings {
  const settings = { ...DEFAULT_SETTINGS }
  const source = { ...row, showOnlyChannels: Array.isArray(row?.showOnlyChannels) ? row.showOnlyChannels.filter((value): value is string => typeof value === 'string') : [] }
  for (const [key, schema] of Object.entries(screenSchema.shape)) {
    const parsed = schema.safeParse(Object.getOwnPropertyDescriptor(source, key)?.value)
    if (parsed.success) Object.assign(settings, { [key]: parsed.data })
  }
  return settings
}

/** The screen's own record, picked out of everything the restaurant has. */
export function settingsFor(rows: Row[], kitchenDisplayId: string): KdsScreenSettings {
  return readScreenSettings(rows.find((row) => row.kitchenDisplayId === kitchenDisplayId))
}

/** Switched off in the backoffice means every screen runs on its own defaults. */
export function effectiveSettings(enabled: boolean, rows: Row[], kitchenDisplayId: string): KdsScreenSettings {
  return enabled ? settingsFor(rows, kitchenDisplayId) : DEFAULT_SETTINGS
}

export function readTillColors(value: unknown): TillColor[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const row = entry as Row
    const tillId = row.tillId
    const color = row.color
    if (typeof tillId !== 'string' || typeof color !== 'string') return []
    return [{ tillId, color, label: typeof row.label === 'string' ? row.label : null }]
  })
}

/** Bigger text and roomier rows, as one number the layout multiplies by. */
export const LAYOUT_SCALE: Record<KdsScreenSettings['layout'], number> = {
  compact: 0.85,
  normal: 1,
  large: 1.25,
}

export const FONT_STACKS: Record<string, string> = {
  system: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  inter: 'Inter, system-ui, sans-serif',
  roboto: 'Roboto, system-ui, sans-serif',
  helvetica: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  arial: 'Arial, Helvetica, sans-serif',
  georgia: "Georgia, 'Times New Roman', serif",
  times: "'Times New Roman', Times, serif",
  courier: "'Courier New', Courier, monospace",
}

/**
 * How many columns of tickets fit. Left to itself it follows the width, because a screen
 * is hung on a wall and nobody measures it first.
 */
export function columnCount(settings: KdsScreenSettings, width: number): number {
  if (settings.columns) return settings.columns
  const target = 340 * LAYOUT_SCALE[settings.layout]
  return Math.max(1, Math.min(8, Math.floor(width / target)))
}

/** The bulk finish button: on for the screen at the pass, off for a station, unless set. */
export function showsSlipCompleteButton(settings: KdsScreenSettings, _resolvedMode: Exclude<DisplayMode, 'auto'>): boolean {
  if (settings.showSlipCompleteButton === 'on') return true
  if (settings.showSlipCompleteButton === 'off') return false
  return true
}

/**
 * A kitchen screen is hung where it is hung: over a pass under bright lights, or in a
 * dim back kitchen. High contrast is not a theme but an accessibility setting — pure
 * black and white for a screen that is read from ten metres away through steam.
 */
export const COLOR_SCHEME_STYLE: Record<KdsScreenSettings['colorScheme'], { root: string; vars: Record<string, string> }> = {
  dark: { root: 'dark', vars: {} },
  light: { root: '', vars: {} },
  'high-contrast': {
    root: 'dark',
    vars: {
      '--background': '#000000',
      '--foreground': '#ffffff',
      '--card': '#000000',
      '--card-foreground': '#ffffff',
      '--muted': '#1a1a1a',
      '--muted-foreground': '#e6e6e6',
      '--border': '#ffffff',
      '--accent': '#1a1a1a',
      '--accent-foreground': '#ffffff',
    },
  },
}
